/**
 * Centralized analytics tracking — dual-writes to GA4 + Supabase analytics_events table.
 */
import { supabase } from "@/integrations/supabase/client";
import { hasAnalyticsConsent } from "@/lib/consent";

type EventParams = Record<string, string | number | boolean | undefined>;

// Session ID persisted per browser tab.
// Lazily created: generating random values at module scope is forbidden in the
// edge runtime (SSR would crash with "Disallowed operation called within global scope").
let sessionId: string | null = null;
const getSessionId = (): string => {
  if (!sessionId) sessionId = crypto.randomUUID();
  return sessionId;
};

// ── Device detection (computed once) ──
const detectDevice = (): "mobile" | "tablet" | "desktop" => {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent;
  // Tablet patterns
  if (/iPad|Tablet|PlayBook|Silk/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) {
    return "tablet";
  }
  if (/Mobi|Android|iPhone|iPod|Opera Mini|IEMobile|BlackBerry/i.test(ua)) {
    return "mobile";
  }
  return "desktop";
};
const DEVICE = detectDevice();

const currentLanguage = (): string => {
  try {
    const lng = (typeof document !== "undefined" ? document.documentElement.lang : "en").toLowerCase();
    return lng.split("-")[0] ?? lng; // normalize en-US → en
  } catch {
    return "en";
  }
};

// Debounce buffer to batch DB inserts
let buffer: Array<{
  event_name: string;
  event_params: EventParams;
  page_path: string;
  referrer: string;
  session_id: string;
  user_agent: string;
}> = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

const flushBuffer = async () => {
  if (!hasAnalyticsConsent()) {
    buffer = [];
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = null;
    return;
  }
  if (buffer.length === 0) return;
  const batch = [...buffer];
  buffer = [];
  flushTimer = null;

  try {
    // Get current user ID if available
    const { data: { session } } = await supabase.auth.getSession();
    if (!hasAnalyticsConsent()) return;
    const userId = session?.user?.id || null;

    const rows = batch.map((e) => ({
      ...e,
      user_id: userId,
    }));

    await supabase.from("analytics_events" as any).insert(rows as any);
  } catch (err) {
    // Silently fail — analytics should never break the app
    console.debug("Analytics flush error:", err);
  }
};

const scheduleFlush = () => {
  if (flushTimer) return;
  flushTimer = setTimeout(flushBuffer, 2000);
};

// Flush on page unload
if (typeof window !== "undefined") {
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushBuffer();
  });
  window.addEventListener("pagehide", flushBuffer);
}

export const track = (eventName: string, params?: EventParams) => {
  // Never create identifiers, buffer events or call a service before consent.
  if (!hasAnalyticsConsent()) return;
  // Auto-enrich every event with language + device for funnel breakdowns
  const enrichedParams: EventParams = {
    ...(params || {}),
    language: currentLanguage(),
    device: DEVICE,
  };

  // GA4
  if (typeof window !== "undefined" && (window as any).gtag) {
    (window as any).gtag("event", eventName, enrichedParams);
  }

  // DB buffer
  buffer.push({
    event_name: eventName,
    event_params: enrichedParams,
    page_path: typeof window !== "undefined" ? window.location.pathname : "",
    referrer: typeof document !== "undefined" ? document.referrer : "",
    session_id: getSessionId(),
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 300) : "",
  });
  scheduleFlush();
};

// ── Navigation & Engagement ──
export const trackNavClick = (destination: string, source: string) =>
  track("nav_click", { destination, source });

export const trackPageScroll = (page: string, depth: number) =>
  track("scroll_depth", { page, depth_percent: depth });

export const trackTimeOnPage = (page: string, seconds: number) =>
  track("time_on_page", { page, duration_seconds: seconds });

// ── Conversion Funnel ──
export const trackSignupIntent = (source: string) =>
  track("signup_intent", { source });

export const trackSignupComplete = () =>
  track("signup_complete");

export const trackLoginComplete = () =>
  track("login_complete");

// ── Landing Page ──
export const trackSplashInteraction = (action: "cta_click" | "close_click") =>
  track("splash_interaction", { event_category: "splash_screen", event_label: action });

export const trackLandingCTA = (ctaName: string, section: string) =>
  track("landing_cta_click", { cta_name: ctaName, section });

// ── Landing readability A/B ──
// Fired exactly once per landing visit, the first time the visitor performs
// any meaningful action (typing in the input, opening the file picker,
// clicking the CTA, the sign-in link, or the try-sample link).
// `elapsed_ms` measures from landing mount to that first action and is the
// canonical "time to first action" readability signal per variant.
export const trackLandingFirstAction = (
  action: "input_focus" | "input_type" | "attach_open" | "cta_click" | "signin_click" | "try_sample" | "submit",
  elapsedMs: number,
  variant: string,
) => track("landing_first_action", { action, elapsed_ms: Math.max(0, Math.round(elapsedMs)), variant });

// Fired every time the primary trial CTA is clicked on the landing page.
// Carries the layout-density variant so the admin can compute CTA click
// rate per variant directly without joining funnel events.
export const trackLandingCtaClick = (placement: string, variant: string) =>
  track("landing_layout_cta_click", { placement, variant });

// ── Trial funnel (step-by-step) ──
// Step 1: landing page viewed by guest
export const trackFunnelLandingView = () =>
  track("funnel_landing_view");
// Step 2: any free-trial CTA clicked (opens info modal or directly)
export const trackFunnelTrialClick = (source: string) =>
  track("funnel_trial_click", { source });
// Step 3: confirmed in modal — about to navigate to sign-in
export const trackFunnelTrialConfirm = (source: string) =>
  track("funnel_trial_confirm", { source });
// Step 4: sign-in / sign-up page viewed
export const trackFunnelSigninView = (source?: string) =>
  track("funnel_signin_view", { source: source || "direct" });
// Step 5 = existing signup_complete

// Landing input → guest modal funnel ──────────────────────────────
// Fired when a guest submits the landing input bar (text/url/yt/file).
export const trackFunnelLandingInputSubmit = (
  inputType: "text" | "url" | "youtube" | "files" | "unknown",
  attachmentCount: number,
) => track("funnel_landing_input_submit", { input_type: inputType, attachment_count: attachmentCount });

// Fired when the post-generation guest conversion modal becomes visible.
export const trackFunnelGuestModalView = (trigger: string) =>
  track("funnel_guest_modal_view", { trigger });

// Fired when an OAuth provider button is clicked from the guest modal / auth page.
export const trackFunnelOAuthStart = (
  provider: "google" | "apple",
  source: string,
) => track("funnel_oauth_start", { provider, source });

// Fired when the SIGNED_IN event fires after an OAuth round-trip.
export const trackFunnelOAuthComplete = (
  provider: string,
  source: string,
  isNewUser: boolean,
) => track("funnel_oauth_complete", { provider, source, is_new_user: isNewUser });

// Fired if OAuth returned an error before redirecting away.
export const trackFunnelOAuthError = (
  provider: "google" | "apple",
  source: string,
  error: string,
) => track("funnel_oauth_error", { provider, source, error: error.slice(0, 200) });

export const trackLandingSection = (sectionName: string) =>
  track("landing_section_view", { section: sectionName });

export const trackFAQOpen = (question: string) =>
  track("faq_open", { question: question.slice(0, 100) });

// ── Sheet Creation Funnel ──
export const trackInputTabSelect = (tab: string) =>
  track("input_tab_select", { tab });

export const trackFileUpload = (fileType: string, fileCount: number) =>
  track("file_upload", { file_type: fileType, file_count: fileCount });

export const trackSheetGenStart = (inputType: string, hasFolder: boolean) =>
  track("sheet_gen_start", { input_type: inputType, has_folder: hasFolder });

export const trackSheetGenComplete = (inputType: string, durationMs: number) =>
  track("sheet_gen_complete", { input_type: inputType, duration_ms: durationMs });

export const trackSheetGenError = (inputType: string, error: string) =>
  track("sheet_gen_error", { input_type: inputType, error: error.slice(0, 200) });

// ── Sheet Interaction ──
export const trackSheetView = (sheetId: string) =>
  track("sheet_view", { sheet_id: sheetId });

export const trackSheetShare = (sheetId: string) =>
  track("sheet_share", { sheet_id: sheetId });

export const trackSheetDownload = (sheetId: string, format: string) =>
  track("sheet_download", { sheet_id: sheetId, format });

export const trackSheetDelete = () =>
  track("sheet_delete");

// ── Revision Activities ──
export const trackQuizStart = (sheetId: string, questionCount: number) =>
  track("quiz_start", { sheet_id: sheetId, question_count: questionCount });

export const trackQuizComplete = (sheetId: string, score: number, total: number) =>
  track("quiz_complete", { sheet_id: sheetId, score, total });

export const trackBlurtStart = (sheetId: string) =>
  track("blurt_start", { sheet_id: sheetId });

export const trackBlurtComplete = (sheetId: string, score: number) =>
  track("blurt_complete", { sheet_id: sheetId, score });

export const trackDefenseStart = (sheetId: string) =>
  track("defense_start", { sheet_id: sheetId });

export const trackTermBlastStart = (sheetId: string) =>
  track("term_blast_start", { sheet_id: sheetId });

export const trackMemoryMatchStart = (sheetId: string) =>
  track("memory_match_start", { sheet_id: sheetId });

export const trackSmartSession = (mode: string, sheetCount: number) =>
  track("smart_session_start", { mode, sheet_count: sheetCount });

// ── Folders & Organization ──
export const trackFolderCreate = () =>
  track("folder_create");

export const trackFolderDelete = () =>
  track("folder_delete");

// ── Study Plan ──
export const trackExamSchedule = (daysUntil: number) =>
  track("exam_schedule", { days_until_exam: daysUntil });

// ── Pro / Paywall ──
export const trackProGateHit = (feature: string) =>
  track("pro_gate_hit", { feature });

export const trackPricingView = (source: string) =>
  track("pricing_view", { source });

export const trackCheckoutStart = (plan: string) =>
  track("checkout_start", { plan });

// ── Support & Settings ──
export const trackSupportChatOpen = () =>
  track("support_chat_open");

export const trackSettingsView = (section: string) =>
  track("settings_view", { section });

// ── Friction / Errors ──
export const trackError = (context: string, message: string) =>
  track("app_error", { context, message: message.slice(0, 200) });

export const trackAbandon = (step: string, reason?: string) =>
  track("abandon", { step, reason });

// ── Page view (called automatically by useGoogleAnalytics but also tracked to DB) ──
export const trackPageView = (path: string) =>
  track("page_view", { page_path: path });

// ── Scroll depth tracker (call once per page) ──
export const initScrollTracker = (pageName: string) => {
  const milestones = [25, 50, 75, 100];
  const reached = new Set<number>();

  const handler = () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight <= 0) return;
    const percent = Math.round((scrollTop / docHeight) * 100);

    for (const m of milestones) {
      if (percent >= m && !reached.has(m)) {
        reached.add(m);
        trackPageScroll(pageName, m);
      }
    }
  };

  window.addEventListener("scroll", handler, { passive: true });
  return () => window.removeEventListener("scroll", handler);
};
